import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import * as z from 'zod';
import pimcoreClient from '../../graphql/pimcore/apollo/pimcoreClient';
import {
    GET_RESOURCE_LIBRARY_ITEM_TYPE_QUERY,
    getResourceLibraryItemQueryVariables,
} from '../../graphql/pimcore/modules/resourceLibrary/queries/getResourceLibraryItemTypeQuery';
import type { ResourceLibraryItemType } from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import type { GetResourceLibraryItemTypeQuery } from '../../graphql/pimcore/modules/resourceLibrary/schemas/generated-types/graphql';
import { ResourcePageContent } from './ResourcePage';
import DebouncedLoader from '../common/DebouncedLoader';

const paramsSchema = z.object({
    id: z.string(),
    lang: z.string().optional(),
});

type SupportedItemType = Exclude<ResourceLibraryItemType, 'internalLink'>;

const ITEM_TYPE_FIELDS: Array<{
    type: SupportedItemType;
    field: keyof GetResourceLibraryItemTypeQuery;
}> = [
    { type: 'asset', field: 'asset' },
    { type: 'article', field: 'article' },
    { type: 'news', field: 'news' },
    { type: 'newProductIntroduction', field: 'newProductIntroduction' },
    { type: 'sitePage', field: 'sitePage' },
];

function useValidatedParams() {
    const params = useParams();
    const result = paramsSchema.safeParse(params);
    if (!result.success) {
        throw new Error('Invalid parameters');
    }
    return result.data;
}

function resolveItemType(
    data: GetResourceLibraryItemTypeQuery | undefined
): SupportedItemType | null {
    if (!data) {
        return null;
    }

    for (const { type, field } of ITEM_TYPE_FIELDS) {
        if (data[field]) {
            return type;
        }
    }

    return null;
}

const GenericResourcePage: React.FC = () => {
    const { id, lang } = useValidatedParams();
    const numericId = Number(id);
    const navigate = useNavigate();

    const skipQuery = !Number.isInteger(numericId);

    const { data, loading, error } = useQuery(GET_RESOURCE_LIBRARY_ITEM_TYPE_QUERY, {
        variables: getResourceLibraryItemQueryVariables(numericId),
        client: pimcoreClient,
        errorPolicy: 'all',
        skip: skipQuery,
    });

    const resolvedItemType = useMemo(() => resolveItemType(data), [data]);

    const hasQueryError = !loading && Boolean(error) && !resolvedItemType;
    const isNotFound = !loading && !error && !resolvedItemType;

    useEffect(() => {
        if (!skipQuery) {
            return;
        }
        console.error('Invalid resource library item id:', id);
        navigate('/404_error', { replace: true });
    }, [skipQuery, id, navigate]);

    useEffect(() => {
        if (!hasQueryError) {
            return;
        }
        console.error('Resource library item lookup failed for id:', id, error);
        navigate('/404_error', { replace: true });
    }, [hasQueryError, id, error, navigate]);

    useEffect(() => {
        if (!isNotFound) {
            return;
        }
        console.error('Resource library item not found for id:', id);
        navigate('/404_error', { replace: true });
    }, [isNotFound, id, navigate]);

    if (skipQuery || hasQueryError || isNotFound) {
        return null;
    }

    return (
        <DebouncedLoader loading={loading} delay={800} size={40} centered={true}>
            {resolvedItemType && (
                <ResourcePageContent itemType={resolvedItemType} id={id} lang={lang} />
            )}
        </DebouncedLoader>
    );
};

export default GenericResourcePage;
